import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

// Design-system
import { FormInputProps, FormValueTypes } from 'design-system';

// Data Layer
import { isSpecialPlan } from 'data-layer-cr/dist/functions/quotations';
import { ActiveConfig, GeneralCatalogItem } from 'data-layer-cr/dist/models';
import QuotationProfile from 'data-layer-cr/dist/models/QuotationProfile';

// Functions
import filterGeneralCatalog from 'src/functions/filterGeneralCatalog';
import mapToCatalogItem from 'src/functions/mapToCatalogItem';
import selectedBrandFound from './selectedBrandFound';

// Models
import CarItem from '../../../../../models/helperTypes/CarItem';

// Redux
import { getBrands, getModels } from '../../../../../store/catalogs/actions';
import { RootState } from '../../../../../store/rootReducer';

// Local components
import CardWithButtons from './CardWithButtons';
import DropDownBrand from './DropDownBrand';

import imageList from './imageList';

function shouldFocusOnAllBrands(selectedBrandID: number): boolean {
  return imageList.some(({ id }) => id === selectedBrandID);
}

export const BrandSelector = ({
  carBrandFieldName,
  carModelFiledName,
  getInputProps,
  setGroupID,
  updateMultipleInputs,
  showBrandIcons,

  // Redux State
  brands,
  activeConfigs,
  car,
  usedProfile,

  // Redux Actions
  getModelsAction,
  getBrandsAction,
  disabled,
}: Props): JSX.Element => {
  const [showPreferredBrands, setShowPreferredBrands] =
    useState<boolean>(showBrandIcons);
  const brandsConfig = activeConfigs['allowed-brands'];

  const brandsList = useMemo(() => {
    const filteredBrands = filterGeneralCatalog(brands, brandsConfig?.brandsId);
    return mapToCatalogItem(filteredBrands);
  }, [brands, brandsConfig]);

  const [selectedBrandId, setSelectedBrandId] = useState<number>(
    selectedBrandFound(brandsList, car.brand),
  );

  const updateBrandState = useCallback(
    (brandCode = '', brandID = -1) => {
      updateMultipleInputs({
        [carBrandFieldName]: brandCode,
        [carModelFiledName]: '',
      });
      setGroupID('');
      setSelectedBrandId(brandID);
    },
    [updateMultipleInputs, carBrandFieldName, carModelFiledName, setGroupID],
  );

  const quotationProfile: QuotationProfile = {
    id: Number(usedProfile),
    description: '',
  };

  const isCommonPlan = !isSpecialPlan(quotationProfile);

  useEffect(() => {
    getBrandsAction();
  }, [getBrandsAction]);

  useEffect(() => {
    if (selectedBrandId !== -1) {
      getModelsAction(selectedBrandId);
    }
  }, [getModelsAction, selectedBrandId]);

  useEffect(() => {
    setSelectedBrandId(selectedBrandFound(brandsList, car.brand));
  }, [brandsList, car.brand]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const CardWithButtonsMemo = useMemo(
    () => (
      <CardWithButtons
        getInputProps={getInputProps}
        carBrandFieldName={carBrandFieldName}
        updateBrandState={updateBrandState}
        setShowPreferredBrands={setShowPreferredBrands}
        selectedBrandId={selectedBrandId}
      />
    ),
    [
      getInputProps,
      carBrandFieldName,
      updateBrandState,
      setShowPreferredBrands,
      selectedBrandId,
    ],
  );

  const DropDownBrandMemo = useMemo(
    () => (
      <DropDownBrand
        showPreferredBrands={showPreferredBrands}
        brandsList={brandsList}
        carBrandFieldName={carBrandFieldName}
        getInputProps={getInputProps}
        getModelsAction={getModelsAction}
        isCommonPlan={isCommonPlan}
        isMobile={isMobile}
        setShowPreferredBrands={setShowPreferredBrands}
        updateBrandState={updateBrandState}
        disabled={disabled}
      />
    ),
    [
      brandsList,
      carBrandFieldName,
      getInputProps,
      getModelsAction,
      isCommonPlan,
      isMobile,
      showPreferredBrands,
      setShowPreferredBrands,
      updateBrandState,
      disabled,
    ],
  );
  // If in mobile mode
  if (isMobile) {
    return DropDownBrandMemo;
  }
  // If brand exist, when duplicate a quote or when the user select a brandID a click back
  // and come back from another step in quote
  if (selectedBrandId !== -1 && !shouldFocusOnAllBrands(selectedBrandId)) {
    return DropDownBrandMemo;
  }
  // If is not a special plan and the user click to see preferred brand
  if (isCommonPlan && showPreferredBrands) {
    return CardWithButtonsMemo;
  }

  return DropDownBrandMemo;
};

export const mapStateToProps = (
  state: RootState,
): {
  brands: GeneralCatalogItem[];
  activeConfigs: ActiveConfig;
  car: CarItem;
  usedProfile: number;
} => ({
  brands: state.catalog.brands,
  activeConfigs: state.dynamicRender.activeConfigs,
  car: state.car.currentCar,
  usedProfile: state.quotation.usedProfile,
});

const reduxActions = {
  getBrandsAction: getBrands,
  getModelsAction: getModels,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  carBrandFieldName: string;
  carModelFiledName: string;
  showBrandIcons: boolean;
  updateMultipleInputs: (newInputData: {
    [key: string]: FormValueTypes;
  }) => void;
  setGroupID: (groupID: string | null | undefined) => void;
  getInputProps: (
    inputName: string,
  ) => FormInputProps<FormValueTypes, FormValueTypes>;
  disabled: boolean;
}

export default connector(BrandSelector);
